import { useEffect, useState } from "react";
import { BRANDS } from "@/lib/brands";
import { useLang } from "@/lib/i18n";

export default function Hero() {
  const { lang, t } = useLang();
  const [tx, setTx] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`)
      .then((r) => r.json())
      .then((d) => alive && setTx(d.transactions))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (tx == null) return;
    const id = setInterval(() => setTx((v) => (v != null ? v + 1 : v)), 1700);
    return () => clearInterval(id);
  }, [tx]);

  const locale = lang === "fr" ? "fr-CH" : "de-CH";

  return (
    <section id="top" data-testid="hero" className="relative pt-32 pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-paper-grid opacity-[0.55] pointer-events-none" />
      <div
        className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(214,40,40,0.12) 0%, rgba(214,40,40,0) 70%)" }}
      />
      <div
        className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(11,27,43,0.10) 0%, rgba(11,27,43,0) 70%)" }}
      />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-7 fade-up">
          <span className="pill" data-testid="hero-live-pill">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--accent))] pulse-dot" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--accent))]" />
            </span>
            {t("hero.livePrefix")} {tx != null ? tx.toLocaleString(locale) : "50 000"} {t("hero.livePillSuffix")}
          </span>
          <span className="pill hidden sm:inline-flex">
            <i className="fa-solid fa-shield-halved text-[hsl(var(--accent))]" />
            {t("hero.swissBuilt")}
          </span>
        </div>

        <h1
          className="font-display font-semibold text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.95] tracking-tight max-w-[16ch] fade-up"
          style={{ animationDelay: "60ms" }}
          data-testid="hero-title"
        >
          {t("hero.titleLead")}
          <span className="italic font-light text-[hsl(var(--accent))]">{t("hero.titleHighlight")}</span>
          {t("hero.titleTail")}
        </h1>

        <p
          className="mt-7 max-w-[58ch] text-[17px] leading-[1.55] text-[hsl(var(--ink-soft))] fade-up"
          style={{ animationDelay: "140ms" }}
          data-testid="hero-sub"
        >
          {t("hero.sub")}
          <strong>{t("hero.subBold")}</strong>
          {t("hero.subEnd")}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3 fade-up" style={{ animationDelay: "220ms" }}>
          <a
            href="#brands"
            data-testid="hero-cta-brands"
            className="group inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-medium hover:bg-[hsl(var(--accent))] transition-colors"
          >
            {t("hero.cta1")}
            <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-[hsl(var(--ink))]/15 hover:border-[hsl(var(--ink))] transition-colors font-medium"
          >
            {t("hero.cta2")}
            <i className="fa-solid fa-comment-dots text-[hsl(var(--accent))]" />
          </a>
        </div>

        <div className="mt-14 flex flex-wrap gap-2 fade-up" style={{ animationDelay: "320ms" }}>
          {t("hero.capabilities").map((c) => (
            <span
              key={c}
              className="text-xs font-medium tracking-tight px-3.5 py-2 rounded-full border border-[hsl(var(--line))] bg-white/60"
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-20 border-y border-[hsl(var(--line))] bg-[hsl(var(--bg-elev))]/60 overflow-hidden">
        <div className="py-5 flex items-center gap-3">
          <span className="ml-6 lg:ml-10 pill" data-testid="hero-portfolio-pill">
            {t("hero.portfolio")}
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="marquee-track flex gap-12 whitespace-nowrap will-change-transform">
              {[...BRANDS, ...BRANDS].map((b, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 text-[15px] font-display font-medium"
                  style={{ color: b.color }}
                >
                  <i className={`fa-solid ${b.icon}`} />
                  {b.name}
                  <span className="text-[hsl(var(--line))] ml-9">/</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
